import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermissions, PagePermissionLevel } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';

interface MenuItem {
  name: string;
  icon: string;
  path: string;
  pageId: string;
  translationKey: string;
  submenu?: { name: string; path: string; pageId: string; translationKey: string; icon?: string }[];
  globalAdminOnly?: boolean;
}

interface MenuCategory {
  category: string;
  translationKey: string;
  items: MenuItem[];
}

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const menuItems: MenuCategory[] = [
  {
    category: 'Analytics',
    translationKey: 'navigation.analytics',
    items: [
      { name: 'Dashboard', icon: 'bi bi-speedometer2', path: '/admin/dashboard', pageId: 'page_dashboard', translationKey: 'navigation.dashboard' },
    ],
  },
  {
    category: 'Infrastructure',
    translationKey: 'navigation.infrastructure',
    items: [
      { name: 'Media Servers', icon: 'bi bi-server', path: '/admin/media-servers', pageId: 'page_media_servers', translationKey: 'navigation.media_servers' },
      { name: 'Tool Runners', icon: 'bi bi-boxes', path: '/admin/tool-runners', pageId: 'page_tool_runners', translationKey: 'navigation.tool_runners' },
      { name: 'Virtual Interfaces', icon: 'bi bi-plug', path: '/admin/virtual-interfaces', pageId: 'page_virtual_interfaces', translationKey: 'navigation.virtual_interfaces' },
      { name: 'Upstream Providers', icon: 'bi bi-cloud', path: '/admin/upstream-providers', pageId: 'page_upstream_providers', translationKey: 'navigation.upstreamProviders', globalAdminOnly: true },
    ],
  },
  {
    category: 'Configuration',
    translationKey: 'navigation.configuration',
    items: [
      { name: 'Agents', icon: 'bi bi-robot', path: '/admin/agents', pageId: 'page_agents', translationKey: 'navigation.agents', submenu: [
        { name: 'Agents', path: '/admin/agents', pageId: 'page_agents', translationKey: 'navigation.agents' },
        { name: 'Outbound Dialers', path: '/admin/outbound-dialers', pageId: 'page_outbound_dialers', translationKey: 'navigation.outboundDialers', icon: 'bi bi-telephone-outbound' },
        { name: 'Vocabulary', path: '/admin/vocabulary', pageId: 'page_vocabulary', translationKey: 'navigation.vocabulary', icon: 'bi bi-book-half' },
      ]},
      { name: 'Tools', icon: 'bi bi-tools', path: '/admin/tools', pageId: 'page_tools', translationKey: 'navigation.tools', submenu: [
        { name: 'Tools List', path: '/admin/tools', pageId: 'page_tools', translationKey: 'navigation.tools_list' },
        { name: 'Tool Tester', path: '/admin/tools/tester', pageId: 'page_tool_tester', translationKey: 'navigation.tool_tester' },
        { name: 'Webhook Tester', path: '/admin/tools/webhook-tester', pageId: 'page_webhook_tester', translationKey: 'navigation.webhook_tester' },
      ]},
      { name: 'Knowledge Bases', icon: 'bi bi-journal-bookmark', path: '/admin/knowledge-bases', pageId: 'page_knowledge_bases', translationKey: 'navigation.knowledgeBases' },
      { name: 'Groups', icon: 'bi bi-people', path: '/admin/groups', pageId: 'page_groups', translationKey: 'navigation.groups', submenu: [
        { name: 'Agent Groups', path: '/admin/groups/agents', pageId: 'page_agent_groups', translationKey: 'navigation.agent_groups' },
        { name: 'Tool Groups', path: '/admin/groups/tools', pageId: 'page_tool_groups', translationKey: 'navigation.tool_groups' },
      ]},
    ],
  },
  {
    category: 'Logs',
    translationKey: 'navigation.logs',
    items: [
      { name: 'Call Logs', icon: 'bi bi-telephone', path: '/admin/call-logs', pageId: 'page_logs', translationKey: 'navigation.call_logs' },
      { name: 'Tool Run Logs', icon: 'bi bi-journal-text', path: '/admin/tool-logs', pageId: 'page_analytics', translationKey: 'navigation.tool_logs' },
    ],
  },
  {
    category: 'Billing',
    translationKey: 'navigation.billing',
    items: [
      { name: 'Billing Report', icon: 'bi bi-receipt', path: '/admin/billing-report', pageId: 'page_billing', translationKey: 'navigation.billing_report', globalAdminOnly: true },
      { name: 'Billing Rates', icon: 'bi bi-tag', path: '/admin/billing-rates', pageId: 'page_billing_rates', translationKey: 'navigation.billing_rates', globalAdminOnly: true },
    ],
  },
  {
    category: 'Administration',
    translationKey: 'navigation.administration',
    items: [
      { name: 'Administrators', icon: 'bi bi-shield-lock', path: '/admin/administrators', pageId: 'page_administrators', translationKey: 'navigation.administrators' },
      { name: 'Tenants', icon: 'bi bi-building', path: '/admin/tenants', pageId: 'page_tenants', translationKey: 'navigation.tenants' },
      { name: 'Email Templates', icon: 'bi bi-envelope', path: '/admin/email-templates', pageId: 'page_email_templates', translationKey: 'navigation.email_templates' },
      { name: 'Audit Logs', icon: 'bi bi-clock-history', path: '/admin/audit-logs', pageId: 'page_logs', translationKey: 'navigation.audit_logs' },
      { name: 'Settings', icon: 'bi bi-gear', path: '/admin/settings', pageId: 'page_settings', translationKey: 'navigation.settings' },
    ],
  },
];

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const { checkPagePermission } = usePermissions();
  const { isGlobalAdmin } = useAuth();
  const location = useLocation();

  const isActivePath = (path: string): boolean => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Check if an item or its submenu items are visible
  const filterItem = (item: MenuItem): boolean => {
    // Global admin only items
    if (item.globalAdminOnly && !isGlobalAdmin) {
      return false;
    }
    if (isGlobalAdmin) {
      return checkPagePermission(item.pageId, PagePermissionLevel.VIEW);
    }
    if (item.pageId === 'page_tenants' && !isGlobalAdmin) {
      return false;
    }
    if (item.pageId === 'page_settings' && !isGlobalAdmin) {
      return false;
    }
    return checkPagePermission(item.pageId, PagePermissionLevel.VIEW);
  };

  const filterSubItem = (sub: { pageId: string }): boolean => {
    return checkPagePermission(sub.pageId, PagePermissionLevel.VIEW);
  };

  const filteredMenuItems = menuItems.map((category) => ({
    ...category,
    items: category.items.filter(filterItem).flatMap((item) => {
      if (item.submenu) {
        const visibleSubs = item.submenu.filter(filterSubItem);
        // Parent item as a regular link
        const parent = item.path
          ? { ...item, submenu: undefined as undefined }
          : null;
        // Sub-items indented with same icon style
        const children = visibleSubs.map((sub) => ({
          name: sub.name,
          icon: item.icon,
          path: sub.path,
          pageId: sub.pageId,
          translationKey: sub.translationKey,
          indent: true as const,
        }));
        return parent ? [parent, ...children] : children;
      }
      return [{ ...item, indent: false as const }];
    }),
  })).filter((category) => category.items.length > 0);

  const sidebarClasses = ['app-sidebar'];
  if (isOpen) sidebarClasses.push('sidebar-open');

  return (
    <aside className={sidebarClasses.join(' ')} id="sidebar" role="complementary">
      <Link to="/admin/dashboard" className="brand-link">
        <span className="brand-text">Voice Agent Admin</span>
      </Link>

      <div className="sidebar">
        <nav className="mt-2">
          <ul className="nav nav-pills nav-sidebar flex-column" role="menu">
            {filteredMenuItems.map((category) => (
              <React.Fragment key={category.category}>
                <li className="nav-header">{t(category.translationKey)}</li>
                {category.items.map((item) => {
                  const isSub = 'indent' in item && item.indent;
                  return (
                    <li key={item.name} className={`nav-item${isSub ? ' nav-sub-item' : ''}`}>
                      <Link
                        to={item.path}
                        className={`nav-link ${isActivePath(item.path) ? 'active' : ''}`}
                        onClick={() => onClose?.()}
                        title={t(item.translationKey)}
                      >
                        <i className={`nav-icon ${item.icon}`}></i>
                        <p>{t(item.translationKey)}</p>
                      </Link>
                    </li>
                  );
                })}
              </React.Fragment>
            ))}
          </ul>
        </nav>
      </div>
    </aside>
  );
}
